import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { fmtNum } from "../lib/format";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useI18n } from "../lib/i18n";
import { useAuth, hasModuleAccess } from "../lib/auth";
import ItemHistoryModal from "../components/ItemHistoryModal";
import AssetOverview from "../components/AssetOverview";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import { Bed, Boxes, WashingMachine, Sparkles, TriangleAlert, Warehouse, ArrowDownToLine, PackageCheck, Clock, DoorOpen, ClipboardCheck } from "lucide-react";

const KPI_CFG = [
  { key: "total_linen_stock", i18n: "kpi.total", icon: Boxes, testid: "kpi-total" },
  { key: "linen_in_rooms", i18n: "kpi.rooms", icon: Bed, testid: "kpi-rooms" },
  { key: "laundry_pending", i18n: "kpi.laundry_pending", icon: WashingMachine, testid: "kpi-laundry-pending" },
  { key: "dirty_linen", i18n: "kpi.dirty", icon: Sparkles, testid: "kpi-dirty" },
  { key: "store_balance", i18n: "kpi.store", icon: Warehouse, testid: "kpi-store" },
  { key: "available_stock", i18n: "kpi.available", icon: PackageCheck, testid: "kpi-available" },
  { key: "missing_linen", i18n: "kpi.missing", icon: ArrowDownToLine, testid: "kpi-missing" },
  { key: "damaged_linen", i18n: "kpi.damaged", icon: TriangleAlert, testid: "kpi-damaged" },
];

function PurchaseChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload || {};
  return (
    <div
      className="border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm"
      data-testid="purchase-chart-tooltip"
    >
      <div className="mb-1 font-semibold text-slate-800">{label}</div>
      <div>Approved purchase quantity: {fmtNum(row.purchase)}</div>
      <div>Approved purchase value: {fmtNum(row.purchase_value)}</div>
      <div>Eligible Purchase Register records: {fmtNum(row.eligible_purchase_records)}</div>
      <div>Approved consumption quantity: {fmtNum(row.consumption)}</div>
    </div>
  );
}

export default function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const { t } = useI18n();
  const { user } = useAuth();
  const canSeeAssets = hasModuleAccess(user, "asset_management", "view");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = canSeeAssets && searchParams.get("tab") === "assets" ? "assets" : "laundry";

  const loadDashboard = useCallback(async () => {
    const [kpiResponse, chartResponse, alertResponse] = await Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/charts"),
      api.get("/dashboard/alerts"),
    ]);
    setKpis(kpiResponse.data);
    setCharts(chartResponse.data);
    setAlerts(alertResponse.data);
  }, []);

  useEffect(() => {
    loadDashboard().catch(() => undefined);
  }, [loadDashboard]);

  useLiveRefresh(loadDashboard, tab === "laundry");

  // recharts v3: Bar-level onClick delivers the datum (payload) directly
  const openHistoryFromBar = (d) => {
    const p = d?.payload || d;
    if (p?.item_id) setHistoryItem({ item_id: p.item_id, name: p.item, size: p.size });
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="section-title" data-testid="page-title">{t("dash.title")}</h1>
          <div className="section-sub">{t("dash.sub")}</div>
        </div>
        <Badge variant="outline" className="border-slate-300">Asia/Kolkata</Badge>
      </div>

      <Tabs value={tab} onValueChange={v => setSearchParams(v === "assets" ? { tab: "assets" } : {})}>
        {canSeeAssets && (
          <TabsList className="mb-4">
            <TabsTrigger value="laundry" data-testid="dash-tab-laundry">Laundry</TabsTrigger>
            <TabsTrigger value="assets" data-testid="dash-tab-assets">Assets</TabsTrigger>
          </TabsList>
        )}

        <TabsContent value="laundry">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6" data-testid="kpi-grid">
        {KPI_CFG.map(({ key, i18n, icon: Icon, testid }) => (
          <Card key={key} className="kpi-card" data-testid={testid}>
            <div className="flex items-center justify-between mb-2">
              <div className="kpi-label">{t(i18n)}</div>
              <Icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="kpi-value">{fmtNum(kpis?.[key])}</div>
            {key === "dirty_linen" && kpis && (
              <div className="text-xs text-slate-500 mt-1">
                Checkout: {fmtNum(kpis.dirty_checkout)} · Holding: {fmtNum(kpis.dirty_holding)}
              </div>
            )}
          </Card>
        ))}
      </div>

      {kpis && kpis.low_stock_alerts?.length > 0 && (
        <Card className="p-4 mb-6 border-amber-300 bg-amber-50" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 mb-2 text-amber-900 font-semibold">
            <TriangleAlert className="w-4 h-4" /> {t("low_stock_alerts")}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            {kpis.low_stock_alerts.map(a => (
              <button
                key={a.item_id}
                type="button"
                onClick={() => setHistoryItem({ item_id: a.item_id, name: a.name, size: a.size })}
                className="flex items-center justify-between border-b border-amber-200 pb-1 text-left w-full hover:bg-amber-100/60 transition-colors cursor-pointer"
                data-testid={`low-stock-row-${a.item_id}`}
              >
                <span className="font-medium text-slate-800 underline decoration-dotted underline-offset-2">{a.name}{a.size ? ` · ${a.size}`: ""}</span>
                <span className="text-amber-900">{a.available} / min {a.threshold}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <Card className="p-4 md:p-5">
          <div className="section-sub mb-3 font-medium text-slate-700">{t("chart.stock_by_location")}</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer minWidth={0} minHeight={260}>
              <BarChart data={charts?.stock_by_location || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="location" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0" }} />
                <Bar dataKey="qty" fill="#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 md:p-5">
          <div className="section-sub mb-3 font-medium text-slate-700">{t("chart.laundry_monthly")}</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer minWidth={0} minHeight={260}>
              <LineChart data={charts?.laundry_monthly || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0" }} />
                <Legend />
                <Line type="monotone" dataKey="sent" stroke="#0f172a" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="received" stroke="#d4af37" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 md:p-5">
          <div className="section-sub mb-3 font-medium text-slate-700">
            {t("chart.items_vs_threshold")}
            <span className="ml-2 text-xs font-normal text-slate-400">(click a bar for item history)</span>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer minWidth={0} minHeight={260}>
              <BarChart data={(charts?.items_vs_threshold || []).slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="item" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={80} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0" }} />
                <Legend />
                <Bar dataKey="available" fill="#0f172a" cursor="pointer" onClick={openHistoryFromBar} />
                <Bar dataKey="threshold" fill="#d4af37" cursor="pointer" onClick={openHistoryFromBar} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-4 md:p-5">
          <div className="section-sub mb-1 font-medium text-slate-700">
            Purchase receipts vs approved consumption — quantity
          </div>
          <div className="mb-3 text-xs text-slate-500" data-testid="purchase-chart-basis">
            Purchases use approved Purchase Register bill dates. Consumption uses approved
            write-off operational dates (Asia/Kolkata).
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer minWidth={0} minHeight={260}>
              <BarChart data={charts?.purchase_vs_consumption || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<PurchaseChartTooltip />} />
                <Legend />
                <Bar dataKey="purchase" name="Purchase quantity" fill="#0f172a" />
                <Bar dataKey="consumption" name="Approved consumption quantity" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <ItemHistoryModal item={historyItem} onClose={() => setHistoryItem(null)} />
        </TabsContent>

        {canSeeAssets && (
          <TabsContent value="assets">
            <AssetOverview />
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
}
